import React, { useEffect } from 'react';
import BaseDataGrid from 'common/components/base-data-grid';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { Box, Checkbox, Typography } from '@mui/material';
import { createStyleSheet } from 'utils/helpers';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'common/hook';
import { RootState } from 'plugins/store';
import { toast } from 'react-toastify';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { handleError } from 'utils/helpers';
import CustomPagination from 'pages/admin/campaign/components/custom-pagination';
import ModalConfirm from 'common/components/modal-confirm';
import CampaignCheckBoxChecked from '../../campaign/components/icons/campaign-check-box-checked';
import CampaignCheckBoxUnchecked from '../../campaign/components/icons/campaign-check-box-uncheck';
import CustomerGridCollumn from './customer-grid-column';
import { ICustomer } from 'types/customer';
import { listClients, deleteClient } from 'services/clientManagement.service';
import { setCustomers } from 'slices/customerSlice';
import {
    ICustomerResponse,
    IListCustomersSearchForm,
    IListClientsPayload,
} from 'types/customer';
import CustomerConstants from '../constants';

const CustomerDataGrid: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const customers = useAppSelector(
        (state: RootState) => state.customer.customers
    );
    const customersSearchForm = useAppSelector(
        (state: RootState) => state.customer.customersSearchForm
    );
    const [openModalDeleteUser, setOpenModalDeleteUser] = React.useState(false);
    const [userSelected, setUserSelected] = React.useState<ICustomer | null>();
    const [rowCount, setRowCount] = React.useState(0);
    const [paginationModel, setPaginationModel] = React.useState({
        page: 0,
        pageSize: 10,
    });

    const transformParams = (
        formState: IListCustomersSearchForm,
        page: number
    ): IListClientsPayload => {
        const params = { ...formState };
        if (formState.status?.includes(CustomerConstants.status.ALL)) {
            params.status = [];
        }
        if (formState.source?.includes(CustomerConstants.source.ALL)) {
            params.source = [];
        }
        return {
            page: page,
            q: {
                search_with_key_cont: params.searchKeyWord,
                status_in: params.status?.join(','),
                created_by_in: params.source?.join(','),
                start_date: params.startDate ?? null,
                end_date: params.endDate ?? null,
            },
        };
    };

    const fetchData = React.useCallback(
        async (page = 1) => {
            try {
                const response = await listClients(
                    transformParams(customersSearchForm, page)
                );
                if (!response.success) {
                    toast.error(response.message);
                    return;
                }
                dispatch(
                    setCustomers(
                        response.data.clients.map(
                            (item: ICustomerResponse) => item.attributes
                        )
                    )
                );
                setRowCount(response.data.total_items);
            } catch (error) {
                handleError(error);
            }
        },
        [customersSearchForm, dispatch]
    );

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const onClickDeleteRow = (row: ICustomer) => {
        setUserSelected(row);
        setOpenModalDeleteUser(true);
    };
    const onClickEditRow = (row: ICustomer) => {
        navigate(`/admin/customer/${row.id}/edit`);
    };
    const navigateToDetail = (row: ICustomer) => {
        navigate(`/admin/customer/${row.id}`);
    };

    const columns: GridColDef[] = CustomerGridCollumn(
        onClickDeleteRow,
        onClickEditRow,
        navigateToDetail
    );

    function labelDisplayedRows({
        from,
        to,
        count,
    }: {
        from: number;
        to: number;
        count: number;
    }) {
        return `Xem ${from} - ${to} trên ${count !== -1 ? count : `hơn ${to}`}`;
    }

    const onCellClick = (cell: GridCellParams) => {
        if (cell.field === 'actions' || cell.field === '__check__') return;
        navigateToDetail(cell.row as ICustomer);
    };

    const onConfirmDeleteClient = async () => {
        if (!userSelected) {
            return;
        }
        const response = await deleteClient(userSelected.id);
        if (!response.success) {
            toast.error(response.message);
            return;
        }
        toast.success(response.message);
        setOpenModalDeleteUser(false);
        dispatch(
            setCustomers(
                customers.filter((item) => item.id !== userSelected.id)
            )
        );
    };

    return (
        <Box component="div">
            <BaseDataGrid
                onCellClick={(cell) => onCellClick(cell)}
                columns={columns}
                rows={customers}
                checkboxSelection
                disableRowSelectionOnClick
                disableColumnMenu
                autoHeight
                sx={styles.dataGrid}
                rowHeight={83}
                localeText={{
                    MuiTablePagination: {
                        labelDisplayedRows,
                    },
                    footerRowSelected: (count) => (
                        <Typography component="span" fontSize={'16px'}>
                            {count} hàng đã chọn
                        </Typography>
                    ),
                }}
                slots={{
                    pagination: CustomPagination,
                    noRowsOverlay: () => (
                        <Box
                            component="div"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height={'100%'}
                        >
                            <Typography component="span" fontSize={'16px'}>
                                Không có dữ liệu
                            </Typography>
                        </Box>
                    ),
                    baseCheckbox: (props) => (
                        <Checkbox
                            {...props}
                            checkedIcon={
                                <CampaignCheckBoxChecked
                                    sx={{
                                        color: (theme) =>
                                            theme.palette.text.primary,
                                    }}
                                />
                            }
                            icon={<CampaignCheckBoxUnchecked />}
                            indeterminateIcon={
                                <IndeterminateCheckBoxIcon
                                    style={{
                                        width: '20px',
                                        height: '20px',
                                    }}
                                />
                            }
                        />
                    ),
                }}
                initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                }}
                pageSizeOptions={[]}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={(newModel) => {
                    setPaginationModel(newModel);
                    fetchData(newModel.page + 1);
                }}
                rowCount={rowCount}
            />
            {openModalDeleteUser && (
                <ModalConfirm
                    open={openModalDeleteUser}
                    onClose={() => setOpenModalDeleteUser(false)}
                    title={'Xác nhận xóa khách hàng'}
                    textOk="Xóa"
                    onOk={onConfirmDeleteClient}
                    content={
                        <Box>
                            Bạn có muốn chắc chắn xóa tài khoản{' '}
                            <b>{`" ${userSelected?.email}"`}</b> này không?
                        </Box>
                    }
                />
            )}
        </Box>
    );
};

const styles = createStyleSheet({
    dataGrid: {
        '--DataGrid-rowBorderColor': 'transparent',
        '.MuiDataGrid-columnHeaders': {
            height: '83px',
            justifyContent: 'center',
        },
        '.MuiDataGrid-columnHeader': {
            padding: 0,
        },
        '.MuiDataGrid-columnHeaderTitle': {
            fontSize: '20px',
            lineHeight: '24px',
            fontWeight: 700,
        },
        '.MuiDataGrid-columnHeaderTitleContainer': {
            display: 'flex',
        },
        '.MuiDataGrid-cell': {
            outline: 'none !important',
            padding: '0',
        },
        '.MuiDataGrid-footerContainer': {
            borderColor: 'transparent',
            marginBottom: '44px',
            marginRight: '54px',
        },
        '.MuiTablePagination-displayedRows': {
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '17px',
        },
        border: 'none',
    },
});

export default CustomerDataGrid;
